/** /import-runs (+ /import-runs/:id/:tab? for the detail dialog overlay)
 *
 * Ops-wide XML import run search across tenants, driven by the reusable
 * QuerySearch query-builder widget (same contract as /delivery-logs/, over
 * Import_Run — UUID row ids). The serialized query lives in `?q=`; a tenant
 * picker (`?tenant=12,845`, one or several operators) locks the query's tenant
 * and injects `tour_operator_id`. Server-side pagination + sorting; all column filtering
 * is delegated to the widget.
 *
 * Grouping: the grid shows TOP-LEVEL rows only (standalone runs + group
 * parents). A parent row expands into a lazy master-detail panel listing its
 * child runs (`?parent=` fetch on expand). Searching by record references
 * goes through Import_Run_Index, whose child rows are mirrored onto the
 * parent — matching a child surfaces its parent row here.
 *
 * The grid SEEDS a 7-day `created` window on first visit (no `?q=`, no
 * `?preset=`) — the delivery-logs convention; Import_Run isn't partitioned,
 * but an unbounded scan of the whole import history is still needlessly slow.
 *
 * Batch ops: Reimport (resubmit) — the dashboard's bulk reimport, through the
 * background-task framework. Upload lives in the page header (manage perm).
 */
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  useGridApiRef,
  type GridPaginationModel,
  type GridRowParams,
  type GridRowSelectionModel,
  type GridSlots,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import * as importRunsApi from '@/api/importRuns';
import type { ImportRunListItem } from '@/api/importRuns';
import { ConsoleApiError, isStatementTimeout } from '@/api/client';
import type { BatchSelection } from '@/api/batch';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { type BatchActionDef, type BatchRefusals } from '@/components/DataGrid/batchTypes';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { gridRefreshToolbar, useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';
import { SearchTimeoutAlert } from '@/components/DataGrid/SearchTimeoutAlert';
import { useBatchAction } from '@/components/DataGrid/useBatchAction';
import { PageHeader } from '@/components/PageHeader';
import { PermissionGate } from '@/components/PermissionGate';
import {
  QuerySearchInput,
  equalityOperator,
  parse,
  serialize,
  toWireAst,
  type FilterNode,
} from '@/components/QuerySearch';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { createdWindowNode } from '@/features/delivery-logs/createdWindow';
import { useGridPresets, type PresetFiltersAdapter } from '@/hooks/useGridPresets';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds } from '@/scope/tenantIds';
import { usePageTenant } from '@/scope/usePageTenant';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { withoutThreadParam } from '@/features/ai-diag/useThreadParam';

import { ImportRunChildren } from './ImportRunChildren';
import { ImportRunDetailDialog } from './detail/ImportRunDetailDialog';
import { ReimportBatchDialog } from './ReimportBatchDialog';
import { UploadImportDialog } from './UploadImportDialog';
import { buildColumns } from './importRunsColumns';
import { buildImportRunNamespaces } from './importRunNamespaces';

const DEFAULT_PAGE_SIZE = 50;

const NO_TENANTS: TenantPickerValue[] = [];

const DEFAULT_HIDDEN = [
  'group',
  'request_id',
  'task_id',
  'processing_started_ts',
  'processing_finished_ts',
  'url',
];

/** Presets store the filter state as the raw `?q=` DSL text — it IS the
 *  canonical form (round-trips through parse/serialize), same as delivery-logs. */
const PRESET_FILTERS: PresetFiltersAdapter = {
  urlParams: ['q'],
  capture: (get) => {
    const q = get('q');
    return q ? { q } : undefined;
  },
  toUrlParams: (filters) => {
    const q = (filters as { q?: unknown } | null)?.q;
    return typeof q === 'string' && q ? { q: q } : ({} as Record<string, string>);
  },
};

/** Drop every `tenant` condition from a tree (the picker owns that filter). */
function stripTenantConds(nodes: FilterNode[]): { nodes: FilterNode[]; removed: boolean } {
  let removed = false;
  const walk = (list: FilterNode[]): FilterNode[] => {
    const out: FilterNode[] = [];
    for (const n of list) {
      if (n.kind === 'cond' && n.namespace === 'tenant') {
        removed = true;
        continue;
      }
      if (n.kind === 'group') {
        const children = walk(n.children);
        if (children.length === 0) continue;
        if (children.length === 1) out.push(children[0]!);
        else out.push({ ...n, children });
        continue;
      }
      out.push(n);
    }
    return out;
  };
  return { nodes: walk(nodes), removed };
}

export function ImportRunsPage() {
  const snackbar = useSnackbar();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: dialogRunId, tab: dialogTab } = useParams<{ id?: string; tab?: string }>();
  const apiRef = useGridApiRef();
  const namespaces = useMemo(() => buildImportRunNamespaces(), []);

  // Effective tenants: URL ?tenant= > console scope (src/scope/usePageTenant.ts),
  // a list (`?tenant=12,845`) — the page takes the whole scope.
  const pageTenant = usePageTenant({ multiple: true });
  // Drives the preset defaults' tenant COLUMN only (shown unless exactly one
  // tenant is named); the filtering value is resolved below, once ?q= has been
  // parsed and scope suppression is known.
  const defaultShowTenant = pageTenant.ids.length !== 1;

  const presets = useGridPresets({
    grid: 'import_runs',
    defaultOrder: useMemo(
      () =>
        buildColumns({
          showTenant: defaultShowTenant,
          onQuickFilter: () => undefined,
          detailPath: (id) => `/import-runs/${id}/info`,
        }).map((c) => c.field),
      [defaultShowTenant],
    ),
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: '-created_ts',
    filters: PRESET_FILTERS,
  });

  const q = presets.getParam('q') ?? '';
  const ordering = presets.ordering;

  const { tree, diagnostics } = useMemo(() => {
    const r = parse(q, namespaces);
    return { tree: r.nodes, diagnostics: r.diagnostics };
  }, [q, namespaces]);

  const lastDiagKey = useRef<string>('');
  useEffect(() => {
    if (diagnostics.length === 0) return;
    const key = `${q}::${diagnostics.length}`;
    if (key === lastDiagKey.current) return;
    lastDiagKey.current = key;
    snackbar.show(
      `Some of your search couldn't be interpreted: ${diagnostics[0]!.message}`,
      'warning',
    );
  }, [diagnostics, q, snackbar]);

  // A `tenant=` condition in ?q= — typed, or carried by a loaded preset —
  // already names a tenant. The ambient console scope then stands down rather
  // than AND-ing a second, conflicting tenant onto the query. An explicitly
  // PICKED tenant still wins and strips those conditions (effect below).
  const queryNamesTenant = useMemo(() => stripTenantConds(tree).removed, [tree]);
  const tenants = pageTenant.fromScope && queryNamesTenant ? NO_TENANTS : pageTenant.values;
  /** What the API's tour_operator_id param carries: `'12,845'` (D1). */
  const tenantApiValue = formatTenantIds(tenants.map((t) => t.id)) ?? undefined;
  // Upload features the page's effective tenants and pre-selects the one when
  // there is exactly one (D9) — none while a `tenant=` condition in ?q= has the
  // scope stand down.
  const pageTenants = pageTenantsFrom(tenantApiValue, tenants);

  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [extendedTimeout, setExtendedTimeout] = useState(false);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 0 }));
  }, [q, pageTenant.raw]);

  const { beforeUrlWrite } = presets;
  const setQuery = useCallback(
    (nodes: FilterNode[]) => {
      const next = new URLSearchParams(sp);
      beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
    },
    [sp, setSp, beforeUrlWrite],
  );

  // Seed the default bounded `created` window on first visit — only when
  // neither an explicit query nor a preset drives the page.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    if (sp.get('q') !== null || sp.get('preset') !== null) return;
    setQuery([createdWindowNode()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a tenant is picked and the tree still carries tenant conditions,
  // strip them and rewrite ?q= (the picker is authoritative for tenant).
  useEffect(() => {
    // Never for a scope-derived tenant: the console scope must not rewrite the
    // user's (or a preset's) query — that would also diverge a loaded preset.
    if (!tenantApiValue || pageTenant.fromScope) return;
    const { nodes, removed } = stripTenantConds(tree);
    if (removed) {
      const next = new URLSearchParams(sp);
      presets.beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
      snackbar.show('Tenant filter replaced by the picker above.', 'info');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantApiValue, tree]);

  const onTenantChange = pageTenant.setTenants;

  // Quick filters from cell popovers: append an equality chip for the
  // namespace (tenant goes through the picker instead — its chip would be
  // stripped right back out).
  const onQuickFilter = useCallback(
    (namespace: string, value: string) => {
      if (namespace === 'tenant') {
        pageTenant.setRaw(value);
        return;
      }
      const cfg = namespaces[namespace];
      const operator = cfg ? equalityOperator(cfg.operators) : null;
      if (!operator) return;
      setQuery([...tree, { kind: 'cond', namespace, operator, value }]);
    },
    [namespaces, tree, setQuery, pageTenant],
  );

  // Detail-dialog routes keep the grid's query string so closing the dialog
  // restores the exact search.
  const detailPath = useCallback(
    (id: string) => `/import-runs/${id}/info${withoutThreadParam(location.search)}`,
    [location.search],
  );

  const lockedFilters = tenantApiValue
    ? { tenant: { operator: 'iexact', value: tenantApiValue } }
    : undefined;
  // The suggestion endpoints here are the bookings autocompletes, which take the list.
  const extraFilterValues = tenantApiValue ? { tour_operator_id: tenantApiValue } : undefined;

  // Toolbar Refresh + auto-refresh. Polling pauses on the extended-timeout
  // tier (each such request is audited at the maximum statement budget).
  const autoRefresh = useGridAutoRefresh({ grid: 'import_runs' });
  const query = useQuery({
    queryKey: [
      'import-runs',
      pagination.page,
      pagination.pageSize,
      ordering,
      tenantApiValue ?? null,
      q,
      extendedTimeout,
    ],
    queryFn: () =>
      importRunsApi.list({
        page: pagination.page + 1,
        page_size: pagination.pageSize,
        ordering,
        ...(tenantApiValue ? { tour_operator_id: tenantApiValue } : {}),
        ...(extendedTimeout ? { timeout: 'extended' as const } : {}),
        filters: toWireAst(tree),
      }),
    placeholderData: keepPreviousData,
    ...autoRefresh.queryOptions(extendedTimeout),
    enabled: !presets.blocking,
  });

  // -------------------------------------------------------------------------
  // Batch: Reimport (resubmit) — raw DataGridPro, so the batch layer is wired
  // by hand (the delivery-logs convention) with STRING (UUID) row ids.
  // -------------------------------------------------------------------------
  const rows = useMemo(() => query.data?.results ?? [], [query.data?.results]);
  const rowCount = query.data?.count ?? 0;

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [allMatching, setAllMatching] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const queryIdentity = useMemo(
    () => JSON.stringify({ q, tenant: tenantApiValue, ordering, pageSize: pagination.pageSize }),
    [q, tenantApiValue, ordering, pagination.pageSize],
  );
  const lastIdentityRef = useRef(queryIdentity);
  useEffect(() => {
    if (lastIdentityRef.current !== queryIdentity) {
      lastIdentityRef.current = queryIdentity;
      if (allMatching) setAllMatching(false);
      setExtendedTimeout(false);
    }
  }, [queryIdentity, allMatching]);

  const selectedIds = useMemo(() => selectionModel.map((i) => String(i)), [selectionModel]);
  const selectedCount = allMatching ? rowCount : selectedIds.length;

  const clearSelection = useCallback(() => {
    setSelectionModel([]);
    setAllMatching(false);
  }, []);

  const handleSelectionChange = useCallback((model: GridRowSelectionModel) => {
    setSelectionModel(model);
    setAllMatching((prev) => (prev ? false : prev));
  }, []);

  const filterQuery = useMemo(() => {
    const usp = new URLSearchParams();
    if (ordering) usp.set('ordering', ordering);
    if (tenantApiValue) usp.set('tour_operator_id', tenantApiValue);
    const wire = toWireAst(tree);
    if (wire.length > 0) usp.set('filters', JSON.stringify(wire));
    return usp.toString();
  }, [ordering, tenantApiValue, tree]);

  const batchAction = useBatchAction({
    resource: 'import_runs',
    queryKey: ['import-runs'],
    onCleared: clearSelection,
  });

  const runStandard = useCallback(
    (
      action: string,
      label: string,
      selection: BatchSelection,
      params?: Record<string, unknown>,
      refusals?: BatchRefusals,
      pinnedQuery?: string,
    ) => batchAction.run({ action, label, selection, params, refusals, filterQuery: pinnedQuery ?? filterQuery }),
    [batchAction, filterQuery],
  );

  const batchActions = useMemo<BatchActionDef[]>(
    () => [
      {
        key: 'resubmit',
        label: 'Reimport…',
        action: 'resubmit',
        permission: 'console_import_runs_manage',
        dialog: (ctx) => <ReimportBatchDialog ctx={ctx} />,
      },
    ],
    [],
  );

  const batchEnabled = batchActions.length > 0;

  const columns = useMemo(
    () =>
      orderColumns(
        buildColumns({ showTenant: tenants.length !== 1, onQuickFilter, detailPath }),
        presets.orderedFields,
      ),
    [tenants.length, onQuickFilter, detailPath, presets.orderedFields],
  );

  // Master-detail: parents expand into a lazy child-run grid; standalone rows
  // have no expander (null content disables the toggle).
  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<ImportRunListItem>) =>
      row.is_parent && row.child_count > 0 ? (
        <ImportRunChildren
          parentId={row.id}
          onQuickFilter={onQuickFilter}
          detailPath={detailPath}
        />
      ) : null,
    [onQuickFilter, detailPath],
  );
  const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

  const apiError =
    query.error instanceof ConsoleApiError &&
    query.error.status === 400 &&
    !isStatementTimeout(query.error)
      ? query.error
      : null;
  const backendMessage =
    (apiError?.body as { filters?: string } | undefined)?.filters ?? apiError?.message ?? null;

  const shownOnce = useRef(false);
  useEffect(() => {
    if (backendMessage && !shownOnce.current) {
      shownOnce.current = true;
      snackbar.show(`Search rejected: ${backendMessage}`, 'error');
    }
    if (!backendMessage) shownOnce.current = false;
  }, [backendMessage, snackbar]);

  const hasSearch = q.trim().length > 0 || tenants.length > 0;

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="import-runs"
        title="Imports"
        subtitle="Search XML import runs across every tenant; expand a group to see its child runs."
        actions={
          <PermissionGate perm="console_import_runs_manage">
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={() => setUploadOpen(true)}
            >
              Upload XML or JSON
            </Button>
          </PermissionGate>
        }
      />

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, alignItems: 'flex-start' }}>
        <Box sx={{ width: 280, flexShrink: 0 }}>
          <TenantPicker multiple value={tenants} onChange={onTenantChange} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <QuerySearchInput
            namespaces={namespaces}
            value={tree}
            onChange={setQuery}
            {...(lockedFilters ? { lockedFilters } : {})}
            {...(extraFilterValues ? { extraFilterValues } : {})}
            placeholder="Search imports…  e.g. booking=BK-1001 & status='import failure'"
          />
        </Box>
      </Stack>

      {isStatementTimeout(query.error) && (
        <SearchTimeoutAlert
          extendedAttempted={extendedTimeout}
          onRetryExtended={!extendedTimeout ? () => setExtendedTimeout(true) : undefined}
          retrying={query.isFetching}
        />
      )}
      {backendMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {backendMessage}
        </Alert>
      )}

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGridPro<ImportRunListItem>
          apiRef={apiRef}
          rows={rows}
          rowCount={rowCount}
          getRowId={(r) => r.id}
          columns={columns}
          loading={query.isLoading || query.isFetching}
          sx={{ flex: 1, minHeight: 360 }}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          pagination
          paginationMode="server"
          sortingMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={() => presets.onColumnOrderChange(gridColumnFieldsSelector(apiRef))}
          pageSizeOptions={[25, 50, 100, 200]}
          checkboxSelection={batchEnabled}
          disableRowSelectionOnClick
          disableColumnFilter
          {...(batchEnabled
            ? {
                rowSelectionModel: selectionModel,
                onRowSelectionModelChange: handleSelectionChange,
                keepNonExistentRowsSelected: true,
              }
            : {})}
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                {hasSearch ? 'No import runs match this search.' : 'Search to find import runs.'}
              </Box>
            ),
            toolbar: GridControlsToolbar as GridSlots['toolbar'],
          }}
          slotProps={{
            loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
            toolbar: {
              refresh: gridRefreshToolbar(query, autoRefresh, { paused: extendedTimeout }),
              hideFilterButton: true,
              exportFileName: 'import-runs',
              presets: presets.toolbar,
              ...(batchEnabled
                ? {
                    batch: {
                      actions: batchActions,
                      ids: selectedIds,
                      allMatching,
                      count: selectedCount,
                      matched: rowCount,
                      filterQuery,
                      runStandard,
                      clearSelection,
                      onSelectAllMatching: () => setAllMatching(true),
                      isPending: batchAction.isPending,
                    },
                  }
                : {}),
            } as GridToolbarProps,
          }}
        />
      )}

      {query.data?.count_is_capped && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          10,000+ results — refine your search to see more precise counts.
        </Typography>
      )}

      <UploadImportDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        pageTenants={pageTenants}
        detailPath={detailPath}
      />

      {dialogRunId && (
        <ImportRunDetailDialog
          runId={dialogRunId}
          tab={dialogTab}
          // The Diagnose tab keeps the open chat in `?thread`; the grid must
          // not inherit it on close (this page carries its search string
          // through every navigation).
          onClose={() => navigate(`/import-runs${withoutThreadParam(location.search)}`)}
          onNavigateTab={(t) => navigate(`/import-runs/${dialogRunId}/${t}${location.search}`)}
          detailPath={detailPath}
        />
      )}
    </Box>
  );
}
